import {createContext, ReactNode, useContext} from 'react';
import {Flow, useFlow} from 'react-chatbotify';
import {useIntl} from 'react-intl';
import {useFormik} from 'formik';
import {useUserContext} from './UserContext';
import {UserAlreadyExistsError} from '../types';
import {useGlobalContext} from './GlobalContext';

type ChatContextType = {
  id: string;
  flow: Flow;
}

interface ChatContextProviderProps {
  children: ReactNode;
}

export const NoUserChatContext = createContext<ChatContextType>({id: '', flow: {}});

export const useNoUserChatContext = () => useContext(NoUserChatContext);

export const NoUserChatContextProvider = ({children}: ChatContextProviderProps) => {
  const intl = useIntl();
  const {api} = useGlobalContext();
  const {createUser, login} = useUserContext();
  const {restartFlow} = useFlow();

  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
    },
    onSubmit: async (values) => {
      await handleSubmit(values.firstName, values.lastName, values.email, values.phone);
    }
  });

  const handleSubmit = async (firstName: string, lastName: string, email: string, phone: string) => {
    let userContactID = '';

    try {
      const flashyResponse = await api.createFlashyContact(
        firstName,
        lastName,
        email,
        phone,
      );
      if (flashyResponse.contactID) {
        userContactID = flashyResponse.contactID;
      }
      await createUser(
        flashyResponse.contactID,
        flashyResponse.firstName,
        flashyResponse.lastName,
        flashyResponse.email,
        flashyResponse.phone,
      );
    } catch (e) {
      if (e instanceof UserAlreadyExistsError) {
        console.error(`User ${userContactID} already exists`);
      } else {
        console.error('Something went wrong:   ', e);
      }
    } finally {
      if (userContactID) {
        login(userContactID);
      } else {
        console.error('User contact ID is not defined!');
      }
    }
  };

  const flow: Flow = {
    start: {
      chatDisabled: false,
      message: () => intl.formatMessage({
        id: 'flow-msg-no-user-found',
        defaultMessage: 'Whoops! How nice to see you here!\uD83D\uDE00 \\nI was taught not to talk to strangers, so that we can continue talking, the following details must be filled out:'
      }),
      transition: {duration: 0},
      path: 'firstName',
    },
    firstName: {
      message: () => intl.formatMessage({
        id: 'flow-msg-no-user-first-name',
        defaultMessage: 'what is your first name?'
      }),
      function: (params) => formik.setFieldValue('firstName', params.userInput),
      path: 'lastName',
    },
    lastName: {
      message: () => intl.formatMessage({
        id: 'flow-msg-no-user-last-name',
        defaultMessage: 'what is your last name?'
      }),
      function: (params) => formik.setFieldValue('lastName', params.userInput),
      path: 'phone',
    },
    phone: {
      message: () => intl.formatMessage({
        id: 'flow-msg-no-user-phone',
        defaultMessage: 'what is your phone?'
      }),
      function: (params) => formik.setFieldValue('phone', params.userInput),
      path: 'email',
    },
    email: {
      message: () => intl.formatMessage({
        id: 'flow-msg-no-user-email',
        defaultMessage: 'what is your email?'
      }),
      function: (params) => formik.setFieldValue('email', params.userInput),
      path: 'submitForm',
    },
    submitForm: {
      function: async () => {
        await formik.submitForm();
        restartFlow();
      },
      transition: {duration: 0},
      path: 'end',
    },
    end: {
      chatDisabled: true
    },
  };

  return (
    <NoUserChatContext.Provider
      value={{
        id: 'ai-chat',
        flow
      }}
    >
      {children}
    </NoUserChatContext.Provider>
  );
};
